import config from 'config';
import { TransactionProduct } from 'types/session-details';
import { OrderCompleteProductInfo, TrackingRevenueInfo } from 'types/tracking';
import { PAYMENT_METHOD_TYPES, REVENUE_TRACKING_HEADER, TRACKING_PURCHASE_FLOW } from 'utils/constants';
import Analytics from './Analytics';

export const initializeSegments = ({ userKey, accountKey, isSystemUser }) => {
  Analytics.init({
    writeKey: config.SEGMENTS.writeKey,
    environment: config.ENV,
  });

  Analytics.identify(userKey, {
    accountKey,
    userKey,
    isSystemUser,
    // TODO: check if isPartner is to be passed as user property as it is passed in billing and cart
  });

  Analytics.group(userKey, {
    // Don't change the groupType name, as the same name is configured under Event mappings under Amplitude destination of Segments Source of Unified Admin
    groupType: 'Account',
    id: accountKey,
  });
};

export const track = (eventName: string, payload: object) => {
  const modifiedEventName = 'OnePay - ' + eventName;

  if (config.SEGMENTS.disabled) {
    console.log('Track: ' + modifiedEventName, payload);
  } else {
    Analytics.track(modifiedEventName, payload);
  }
};

const trackingPurchaseProductInfo = (orderItem: TransactionProduct): OrderCompleteProductInfo => {
  const productId = orderItem.productName;
  const orderItemAmount = orderItem.amount;
  let orderType = '';

  if (orderItemAmount > 0) {
    orderType = 'purchase';
  } else if (orderItemAmount < 0) {
    orderType = 'refund';
  } else {
    orderType = 'free';
  }

  return {
    productId: productId,
    productFamilyKey: orderItem.productGroup,
    revenue: Number(orderItemAmount.toFixed(2)),
    quantity: orderItem.quantity,
    revenueType: orderType,
    paymentPlan: orderItem.planTermType,
  };
};

export const getPurchaseFlow = (isEcomClient: boolean, sessionCancelUrl: string): string => {
  let purchaseFlow = '';

  if (isEcomClient) purchaseFlow = TRACKING_PURCHASE_FLOW.ecom;
  else if (sessionCancelUrl.includes('quotes')) purchaseFlow = TRACKING_PURCHASE_FLOW.quote;
  else if (sessionCancelUrl.includes('subscriptions')) purchaseFlow = TRACKING_PURCHASE_FLOW.subscription;
  else if (sessionCancelUrl.includes('payments-due')) purchaseFlow = TRACKING_PURCHASE_FLOW.renewals;
  else if (sessionCancelUrl.includes('invoices')) purchaseFlow = TRACKING_PURCHASE_FLOW.invoice;

  return purchaseFlow;
};

export const getRevenueTrackingType = (isEcomClient: boolean, sessionCancelUrl: string): string => {
  let revenueTrackingType = '';

  if (isEcomClient) revenueTrackingType = REVENUE_TRACKING_HEADER.ECOM;
  else if (sessionCancelUrl.includes('quotes')) revenueTrackingType = REVENUE_TRACKING_HEADER.QUOTE;
  else if (sessionCancelUrl.includes('subscriptions')) revenueTrackingType = REVENUE_TRACKING_HEADER.SUBSCRIPTION;
  else if (sessionCancelUrl.includes('payments-due')) revenueTrackingType = REVENUE_TRACKING_HEADER.RENEWALS;
  else if (sessionCancelUrl.includes('invoices')) revenueTrackingType = REVENUE_TRACKING_HEADER.INVOICE;
  return revenueTrackingType;
};

export const getPaymentTrackingType = (paymentType: string, allowedPaymentType: string[]) => {
  return allowedPaymentType.find((type) => PAYMENT_METHOD_TYPES[paymentType]?.includes(type)) || paymentType;
};

export const trackRevenue = (payload: TrackingRevenueInfo): void => {
  const { orderList, purchaseFlow, currencyCode, paymentMethod, quoteId, total, clientURL } = payload;

  if (Array.isArray(payload.orderList)) {
    const items = orderList.reduce((acc: OrderCompleteProductInfo[], orderItem) => {
      const productDetails = trackingPurchaseProductInfo(orderItem);
      acc.push(productDetails);
      return acc;
    }, []);

    const orderCompletePayload = {
      order_id: quoteId,
      affiliation: clientURL,
      revenue: total,
      currency: currencyCode,
      products: items,
      paymentMethod,
      purchaseFlow,
      processedOn: 'onePayApp',
    };

    if (!config.SEGMENTS.disabled) {
      Analytics.track('Order Completed', orderCompletePayload);
    } else {
      console.log('Track: Order Completed', orderCompletePayload);
    }
  }
};

export default track;
